import axios from "axios";
import http from "lib/http";
import { uniq } from "lodash";
import { formatFilters } from "utils/filterMapUtils";
import { type PeopleAvailablePackOverview } from "features/Accounts/PackAssignmentModal/types";
import {
  type Accounts,
  type PacksListItem,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import PacksOwnership from "../../../../enums/licensing/packsOwnership";
import PackRequestTypes from "../../../../enums/packRequestTypes";
import { type UserWithPackAccessType } from "../../../../interfaces";
import { type PackContextResponse } from "../../../../interfaces/licenseConfirmation";
import {
  buildQueryFromArray,
  serializeArrayAndFilterNullable,
  processPerformanceParams,
  type Filters,
} from "../../../../utils/queryUtils";
import { escapeTerm } from "../../../../utils/searchUtils";
import KnownHttpHeaders from "../../../Application/services/knownHttpHeaders";
import {
  type ConvertTrialLicenseRequest,
  type AddPacksToAccountRequest,
  type GetPackPeopleLicensesRequest,
  type GetPacksRequest,
  type GetPurchasePriceRequest,
  type ProcessPaymentRequest,
  type PurchasePackFailureResponse,
  type PurchasePackSuccessResponse,
  type PurchasePriceFailureResponse,
  type PurchasePriceSuccessResponse,
  type UpdateAccountPackLicenseRequest,
  type UpdatePackAccountsVisibilityRequest,
  type UpdatePackAccountTypesVisibilityRequest,
  type UpdatePackContentRequest,
  type UpdatePackIsPrivate,
} from "../types/requests";
import { type IPacksDataService } from "../types/services";
import {
  type CustomerAssignedLicensesRequest,
  type CustomerAssignedLicensesResponse,
  type CustomerDepartmentUsageRequest,
  type CustomerPacksRequest,
  type CustomerPacksSummaryRequest,
  type CustomerPacksSummaryResponse,
  type DepartmentUsage,
  type PackSettingsPayload,
  type EnablePackRequest,
  type EnablePackTrialRequest,
  type Pack,
  type PackOverview,
  type PackRequest,
} from "../types/state";
import { packFilterTypes } from "../types/urlParams";
import { type CustomerPacks } from "features/Reporting/types/packs";
import type { AddOnsEngagementResponse, AddOnsAccountRow, AddOnDetailRow } from "features/Reporting/types/addOns";

export const CUSTOMER_DEPARTMENT_USAGE_ENDPOINT = "/api/reports/v1/licenses/customer/assigned/department";
export const CUSTOMER_ASSIGNED_LICENSES_ENDPOINT = "/api/reports/v1/licenses/customer/assigned";
export const CUSTOMER_PACKS_ENDPOINT = "/api/reports/v1/licenses/customer/packs";
export const CUSTOMER_PACKS_SUMMARY_ENDPOINT = "/api/reports/v1/licenses/customer/packs/summary";

const packsDataService: IPacksDataService = {
  getPacksAsync(data: GetPacksRequest, filters: Filters) {
    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    const apiUrl = filters.ownership === PacksOwnership.Available ? "/api/v3/packs/available" : "/api/v2/packs/own";
    const { ownership, ...filtersWithoutOwnership } = filters;

    const formattedFilters = formatFilters(filtersWithoutOwnership, packFilterTypes);

    config.params = { ...config.params, ...formattedFilters, searchTerm: escapeTerm(data.searchTerm) };
    return axios.get(apiUrl, config);
  },

  getPacksWithCancel(data: GetPacksRequest, filters: Filters) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = filters.ownership === PacksOwnership.Available ? "/api/v3/packs/available" : "/api/v2/packs/own";
    const { ownership, ...filtersWithoutOwnership } = filters;

    const formattedFilters = formatFilters(filtersWithoutOwnership, packFilterTypes);

    config.params = { ...config.params, ...formattedFilters };

    return { getDataPromise: axios.get<PackOverview[]>(apiUrl, config), cancel: source.cancel };
  },

  getPackUserContentAssignmentInfoAsync(packIds: number[], userIds?: number[], groupIds?: number[], count?: number) {
    const packsQuery = buildQueryFromArray("packIds", uniq(packIds));
    const usersQuery = userIds && userIds.length > 0 ? `&${buildQueryFromArray("userIds", uniq(userIds))}` : "";
    const groupsQuery = groupIds && groupIds.length > 0 ? `&${buildQueryFromArray("groupIds", uniq(groupIds))}` : "";
    const countQuery = count !== undefined ? `&count=${count}` : "";

    return axios.get(`/api/licenses/available?${packsQuery}${usersQuery}${groupsQuery}${countQuery}`);
  },

  getPackUserContentAssignmentInfoV2Async(packIds: number[], userIds?: number[], groupIds?: number[], count?: number) {
    const params = [
      buildQueryFromArray("packIds", uniq(packIds)),
      userIds?.length ? buildQueryFromArray("userIds", uniq(userIds)) : null,
      groupIds?.length ? buildQueryFromArray("groupIds", uniq(groupIds)) : null,
      count !== undefined ? `count=${count}` : null,
    ]
      .filter(Boolean)
      .join("&");

    return axios.get(`/api/v2/licenses/available?${params}`);
  },

  getUserLicenseInfoByGroupMembers(packIds: number[], count: number) {
    const packsQuery = buildQueryFromArray("packIds", uniq(packIds));

    return axios.get(`/api/licenses/group-members/available?${packsQuery}&count=${count}`);
  },

  getPackUserFlowAssignmentInfoAsync(packIds: number[], flowId: number) {
    const packsQuery = buildQueryFromArray("packIds", uniq(packIds));

    return axios.get(`/api/licenses/flow-people/available?${packsQuery}&flowId=${flowId}`);
  },
  getPackUserFlowAssignmentInfoV2Async(packIds: number[], flowId: number) {
    const packsQuery = buildQueryFromArray("packIds", uniq(packIds));

    return axios.get(`/api/v2/licenses/flow-people/available?${packsQuery}&flowId=${flowId}`);
  },
  getPackPeopleAssignmentInfoAsync(packIds: number[], packId: number) {
    const packsQuery = buildQueryFromArray("packIds", uniq(packIds));

    return axios.get(`/api/licenses/pack-people/available?${packsQuery}&packId=${packId}`);
  },

  getContentPacksInfoAsync(type: string, ids: string[]) {
    const query = buildQueryFromArray("ids", ids);
    return axios.get(`/api/content/${type}/packs?${query}`);
  },

  getContentPacksInfoAsyncV2(type: string, ids: string[]) {
    const query = buildQueryFromArray("ids", ids);
    return axios.get<PackContextResponse>(`/api/v2/content/${type}/packs?${query}`);
  },

  getFlowConflictsInfoAsync(ids: string[]) {
    const query = buildQueryFromArray("ids", ids);
    return axios.get(`/api/v4/content/flows/packs/conflicts?${query}`);
  },

  getPackConflictsInfoAsync(ids: string[]) {
    const query = buildQueryFromArray("ids", ids);
    return axios.get(`/api/v4/packs/conflicts?${query}`);
  },

  getDraftPackConflictsInfoAsync(id: number) {
    return axios.get(`/api/v4/packs/${id}/conflicts`);
  },

  getRequestedPacksLicensesAsync(assignedAccountId: number, packIds: number[]) {
    const packsQuery = buildQueryFromArray("packIds", uniq(packIds));
    return axios.get(`/api/packs/requests?accountId=${assignedAccountId}&${packsQuery}`);
  },

  updatePackAsync(data: Pack) {
    return axios.put(`/api/packs/${data.id}/info`, data);
  },

  getPackAsync(id: number) {
    return axios.get(`/api/packs/${id}`);
  },

  getPackContentAsync(id: number) {
    return axios.get(`/api/packs/${id}/content`);
  },

  updatePackContentAsync(id: number, packContent: UpdatePackContentRequest) {
    return axios.put(`/api/packs/${id}/content`, packContent);
  },

  getPackListingAsync(id: number) {
    return axios.get(`/api/packs/${id}/overview`);
  },

  getPurchasedPackAsync(id: number) {
    return axios.get(`/api/packs/${id}/purchased`);
  },

  requestPackAsync(packRequest: PackRequest, isAdditionalPurchaseEnabled: boolean) {
    const payload = isAdditionalPurchaseEnabled
      ? packRequest
      : { ...packRequest, typeId: PackRequestTypes.RequestPack };

    return axios.post(`/api/packs/requests`, payload);
  },

  enablePackTrialAsync(req: EnablePackTrialRequest) {
    return axios.post(`/api/v2/accounts/packs/licenses/trial`, {
      packInfos: [{ packId: req.packId, licensesCount: req.licensesCount }],
    });
  },

  enablePackAsync(request: EnablePackRequest) {
    const data: AddPacksToAccountRequest = { packInfos: [{ ...request, isRequested: false, edited: false }] };
    return axios.post(`/api/v2/account/packs`, data);
  },

  addPacksToAccountsAsync(accountId: number, request: AddPacksToAccountRequest) {
    return axios.post(`/api/v2/accounts/${accountId}/packs`, request);
  },

  extendPacksLicensesToAccountsAsync(accountId: number, request: AddPacksToAccountRequest) {
    return axios.post(`/api/accounts/${accountId}/packs/licenses`, request);
  },

  updateAccountPackLicenseAsync(accountId: number, request: UpdateAccountPackLicenseRequest) {
    return axios.put(`/api/accounts/${accountId}/packs/licenses`, request);
  },

  getPackFiltersAsync() {
    return axios.get("/api/packs/filters");
  },

  getPacksPublishersAsync() {
    return axios.get("/api/packs/publishers", {});
  },

  async isTitleUniqueAsync(title: string, id: number) {
    const config = {
      params: { title, id },
    };

    const response = await axios.get("/api/packs/is-title-unique", config);
    return response.data;
  },

  getPackTypesAsync() {
    return axios.get("/api/packs/types");
  },

  getPackLicensesTypesAsync() {
    return axios.get("/api/packs/license-types");
  },

  getPackTrialPeriodsAsync() {
    return axios.get("/api/packs/trial-periods");
  },

  getPackBillingTermsAsync() {
    return axios.get("/api/packs/billing-terms");
  },

  getPackPricingTypesAsync() {
    return axios.get("/api/packs/pricing-types");
  },

  async getAccountPacks(accountId: number, data: GetPacksRequest, filters: Filters) {
    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    if (filters) {
      const formattedFilters = formatFilters(filters, packFilterTypes);
      config.params = { ...config.params, ...formattedFilters };
    }

    const apiUrl = `/api/v2/accounts/${accountId}/packs`;
    const response = await axios.get(apiUrl, config);
    return {
      items: response.data,
      count: parseInt(response.headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  getAccountPacksWithCancel(accountId: number, data: GetPacksRequest, filters: Filters) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = `/api/v2/accounts/${accountId}/packs`;
    const formattedFilters = formatFilters(filters, packFilterTypes);
    config.params = { ...config.params, ...formattedFilters };

    return { getDataPromise: axios.get<PackOverview[]>(apiUrl, config), cancel: source.cancel };
  },

  getGroupsPacksAsync(groupIds: number[]) {
    const groupsQuery = buildQueryFromArray("groupIds", uniq(groupIds));
    return axios.get(`/api/groups/packs?${groupsQuery}`);
  },

  getUserPacksWithCancel(userId: number, data: GetPacksRequest) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = `/api/v4/users/${userId}/packs`;

    return { getDataPromise: axios.get<PackOverview[]>(apiUrl, config), cancel: source.cancel };
  },

  getGroupPacksWithCancel(groupId: number, data: GetPacksRequest) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = `/api/v4/groups/${groupId}/packs`;

    return { getDataPromise: axios.get<PackOverview[]>(apiUrl, config), cancel: source.cancel };
  },

  getPackUserLicensesAsync: (packId: number, request: GetPackPeopleLicensesRequest) => {
    const config = {
      params: request,
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    const apiUrl = `/api/v4/packs/${packId}/users`;

    return axios.get(apiUrl, config);
  },

  getPackGroupLicensesAsync: (packId: number, request: GetPackPeopleLicensesRequest) => {
    const config = {
      params: request,
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    const apiUrl = `/api/v4/packs/${packId}/groups`;

    return axios.get(apiUrl, config);
  },

  getPackVisibilityAccountIdsAsync(packId: number) {
    return axios.get(`/api/packs/${packId}/visibility/accounts`);
  },

  updatePackAccountsVisibilityAsync(packId: number, request: UpdatePackAccountsVisibilityRequest) {
    return axios.put(`/api/packs/${packId}/visibility/accounts`, request);
  },

  getAccountTypesVisibilityForPackAsync(packId: number) {
    return axios.get(`/api/packs/${packId}/visibility/account-types`);
  },

  updateAccountTypesVisibilityForPackAsync(packId: number, request: UpdatePackAccountTypesVisibilityRequest) {
    return axios.put(`/api/packs/${packId}/visibility/account-types`, request);
  },

  getUserAvailablePacksWithCancel(userId: number, data: GetPacksRequest) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = `/api/users/${userId}/available-packs`;

    return { getDataPromise: axios.get<PeopleAvailablePackOverview[]>(apiUrl, config), cancel: source.cancel };
  },

  getGroupAvailablePacksWithCancel(groupId: number, data: GetPacksRequest) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = `/api/groups/${groupId}/available-packs`;

    return { getDataPromise: axios.get<PeopleAvailablePackOverview[]>(apiUrl, config), cancel: source.cancel };
  },

  async getAvailablePackUsers({ packId, ...params }) {
    const url = `/api/v4/packs/${packId}/available-users`;
    const { data, headers } = await axios.get<UserWithPackAccessType[]>(url, {
      params,
      paramsSerializer: serializeArrayAndFilterNullable,
    });

    return {
      items: data.map((item) => ({ ...item, id: item.userId })),
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  getAccountPackLicense(packId: number, accountId: number) {
    return axios.get(`/api/accounts/${accountId}/packs/${packId}/licenses`);
  },

  async getPackUsageCount(packId: number) {
    const { data } = await axios.get(`/api/packs/usages?packId=${packId}`);
    return data.usageCount;
  },

  async deletePack(id: number) {
    return axios.delete(`/api/packs/${id}`);
  },

  async archivePack(accountId: number, packId: number) {
    return axios.post(`/api/accounts/${accountId}/packs/${packId}/archive`);
  },

  restoreAccountPackAsync(accountId: number, request: AddPacksToAccountRequest) {
    return axios.post(`/api/accounts/${accountId}/packs/unarchive`, request);
  },

  convertTrialAccountPackAsync(accountId: number, packId: number, request: ConvertTrialLicenseRequest) {
    return axios.post(`/api/accounts/${accountId}/packs/${packId}/convert`, request);
  },

  async addContentToPacks(packIds: number[], contentIds: number[], contentType: string) {
    return axios.post("/api/packs/content", { contentType, packIds, contentIds });
  },

  async getPackIsPrivate(packId: number) {
    const result = await axios.get(`/api/packs/${packId}/is-private`);
    return result.data;
  },

  updatePackIsPrivateAsync(packId: number, request: UpdatePackIsPrivate) {
    return axios.put(`/api/packs/${packId}/is-private`, request);
  },

  async getOwnLicenses() {
    const response = await http.get<number[]>(`/api/me/licenses`);
    return response;
  },

  getPacksFilterOptions(
    contentType:
      | "email"
      | "flow"
      | "video"
      | "assessment"
      | "survey"
      | "externalEvent"
      | "pdf"
      | "embeddedVideo"
      | "campaign"
      | "link"
      | "scorm",
    showPurchased: boolean,
    moboId?: string,
  ) {
    return axios.get(
      `/api/accounts/content/${contentType}/packs`,
      moboId ? { headers: { moboId }, params: { showPurchased } } : { params: { showPurchased } },
    );
  },

  async getUserGroupLicenses(userIds: number[], packIds: number[]) {
    const response = await axios.get("/api/users/licenses/inherited", {
      params: { userIds, packIds },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
    return response.data;
  },

  getAssociatedPacksAsync(contentType: string, contentId: number) {
    return axios.get(`/api/content/${contentType}/${contentId}/packs`);
  },

  updatePackSettingsAsync(id: number, settings: PackSettingsPayload) {
    return axios.put(`/api/packs/${id}/settings`, settings);
  },

  getPackSettingsAsync(id: number) {
    return axios.get(`/api/packs/${id}/settings`);
  },

  getCustomerDepartmentUsage: async (params: CustomerDepartmentUsageRequest, signal?: AbortSignal) => {
    return axios.get<DepartmentUsage[]>(CUSTOMER_DEPARTMENT_USAGE_ENDPOINT, {
      params,
      signal,
    });
  },

  getCustomerAssignedLicenses: async (params: CustomerAssignedLicensesRequest, signal?: AbortSignal) => {
    return axios.get<CustomerAssignedLicensesResponse>(CUSTOMER_ASSIGNED_LICENSES_ENDPOINT, {
      params,
      signal,
    });
  },

  getCustomerPacks: async (params: CustomerPacksRequest, signal?: AbortSignal) => {
    return axios.get<CustomerPacks[]>(CUSTOMER_PACKS_ENDPOINT, {
      params,
      signal,
    });
  },

  getCustomerPacksSummary: async (params: CustomerPacksSummaryRequest, signal?: AbortSignal) => {
    return axios.get<CustomerPacksSummaryResponse>(CUSTOMER_PACKS_SUMMARY_ENDPOINT, {
      params,
      signal,
    });
  },

  getAddOnsEngagement: async (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
    return axios.get<AddOnsEngagementResponse>("/api/reports/v1/addons/engagement", {
      params,
      paramsSerializer: processPerformanceParams,
      signal,
    });
  },

  getAddOnsAccounts: async (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
    return axios.get<AddOnsAccountRow[]>("/api/reports/v1/addons/accounts", {
      params,
      paramsSerializer: processPerformanceParams,
      signal,
    });
  },

  getAddOnsAccountsExport: async (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
    return axios.get("/api/dataexport/v1/addons/accounts", {
      params,
      responseType: "blob",
      paramsSerializer: processPerformanceParams,
      signal,
    });
  },

  getAccountAddOns: async (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
    return axios.get<AddOnDetailRow[]>("/api/reports/v1/addons/account-addons", {
      params,
      paramsSerializer: processPerformanceParams,
      signal,
    });
  },

  getAccountAddOnsExport: async (params: PerformanceWithAccountFilter, signal?: AbortSignal) => {
    return axios.get("/api/dataexport/v1/addons/account-addons", {
      params,
      paramsSerializer: processPerformanceParams,
      responseType: "blob",
      signal,
    });
  },

  async processPayment(data: ProcessPaymentRequest) {
    const response = await axios.post<PurchasePackSuccessResponse | PurchasePackFailureResponse>(
      "/api/packs/purchase",
      data,
    );

    return response.data;
  },

  getPurchasePrice(params: GetPurchasePriceRequest) {
    return axios.get<PurchasePriceSuccessResponse | PurchasePriceFailureResponse>(`/api/packs/total-price`, {
      params,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getAccountPacksPage: async (
    accounts?: Accounts[] | number[] | string[],
    includeMyData?: boolean,
    type?: string,
  ): Promise<PacksListItem[]> => {
    const config = {
      params: {
        accounts,
        includeMyData,
        type,
      },
      paramsSerializer: processPerformanceParams,
    };
    const response = await axios.get<Array<{ PackId: number; PackTitle: string }>>(
      `/api/reports/v1/licenses/pack-select`,
      config,
    );
    const mapped: PacksListItem[] = response.data.map((pack) => ({ value: pack.PackId, text: pack.PackTitle }));
    return mapped;
  },
};

export default packsDataService;
